import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MasterService } from '../../services/master.service';
import { WarehouseService } from '../../services/warehouse.service';
import { HttpClient } from '@angular/common/http';
import { BASE_URL } from '../../../config/config';
import Chart from 'chart.js/auto';
import { Router } from '@angular/router';

@Component({
  selector: 'app-warehouse-v2-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './warehouse-v2-dashboard.component.html',
  styleUrls: ['./warehouse-v2-dashboard.component.scss']
})
export class WarehouseV2DashboardComponent implements OnInit, AfterViewInit {
  warehouses: any[] = [];
  selectedWarehouseId: string = '';
  isLoading: boolean = false;

  // Widget data
  activeStocks: number = 0;
  expiringStocks: number = 0;
  expiredStocks: number = 0;
  shipmentSummaryData: any = null;
  categoryStocksData: any = null;

  @ViewChild('shipmentSummaryChart', { static: false }) shipmentSummaryChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('categoryStocksChart', { static: false }) categoryStocksChartRef!: ElementRef<HTMLCanvasElement>;
  private shipmentChartInstance: any;
  private categoryChartInstance: any;
  private chartsInitialized = false;

  constructor(
    private masterService: MasterService,
    private warehouseService: WarehouseService,
    private http: HttpClient,
    private router: Router
  ) {}

  ngOnInit() {
    // Use static warehouse list for now
    this.warehouses = [
      { id: 1, name: 'Mater Private Dublin' },
      { id: 2, name: 'Mater Private Cork' },
      { id: 3, name: 'Mater Private Galway' },
      { id: 4, name: 'UK Warehouse' },
      { id: 5, name: 'Warehouse' }
    ];
    this.selectedWarehouseId = '4';
    this.onWarehouseChange();
  }

  getWarehouses() {
    this.masterService.getIndexGet('/warehouses').subscribe((res) => {
      if (res.data && Array.isArray(res.data)) {
        this.warehouses = res.data;
        // Set default warehouse to first one if available
        if (this.warehouses.length > 0) {
          this.selectedWarehouseId = this.warehouses[0].id.toString();
          this.onWarehouseChange();
        }
      }
    });
  }

  ngAfterViewInit() {
    this.chartsInitialized = true;
    this.renderCharts();
  }

  onWarehouseChange() {
    this.fetchCountCards();
    this.fetchShipmentSummary();
    this.fetchCategoryStocks();
  }

  fetchCountCards() {
    if (!this.selectedWarehouseId) return;
    this.isLoading = true;
    const token = localStorage.getItem('token');
    this.http.get(`${BASE_URL.URL}/warehouses/${this.selectedWarehouseId}/products?counts=active,expiring,expired`, {
      headers: { 'Authorization': 'Bearer ' + token }
    }).subscribe({
      next: (res: any) => {
        this.activeStocks = res?.data?.active_stocks || 0;
        this.expiringStocks = res?.data?.expiring_stocks || 0;
        this.expiredStocks = res?.data?.expired_stocks || 0;
      },
      error: () => {
        this.activeStocks = 0;
        this.expiringStocks = 0;
        this.expiredStocks = 0;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  fetchShipmentSummary() {
    if (!this.selectedWarehouseId) return;
    this.isLoading = true;
    const token = localStorage.getItem('token');
    this.http.get(`${BASE_URL.URL}/charts/warehouses/${this.selectedWarehouseId}/shipments`, {
      headers: { 'Authorization': 'Bearer ' + token }
    }).subscribe({
      next: (res: any) => {
        this.shipmentSummaryData = res?.data || null;
        if (this.chartsInitialized) this.renderShipmentChart();
      },
      error: () => {
        this.shipmentSummaryData = null;
        if (this.chartsInitialized) this.renderShipmentChart();
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  fetchCategoryStocks() {
    if (!this.selectedWarehouseId) return;
    this.isLoading = true;
    const token = localStorage.getItem('token');
    this.http.get(`${BASE_URL.URL}/charts/warehouses/${this.selectedWarehouseId}/stocks`, {
      headers: { 'Authorization': 'Bearer ' + token }
    }).subscribe({
      next: (res: any) => {
        this.categoryStocksData = res?.data || null;
        if (this.chartsInitialized) this.renderCategoryChart();
      },
      error: () => {
        this.categoryStocksData = null;
        if (this.chartsInitialized) this.renderCategoryChart();
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  renderCharts() {
    if (this.chartsInitialized) {
      this.renderShipmentChart();
      this.renderCategoryChart();
    }
  }

  renderShipmentChart() {
    if (!this.shipmentSummaryChartRef || !this.shipmentSummaryData) return;
    if (this.shipmentChartInstance) {
      this.shipmentChartInstance.destroy();
    }
    const ctx = this.shipmentSummaryChartRef.nativeElement.getContext('2d');
    if (!ctx) return;
    this.shipmentChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.shipmentSummaryData.labels || [],
        datasets: this.shipmentSummaryData.datasets || []
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true }
        }
      }
    });
  }

  renderCategoryChart() {
    if (!this.categoryStocksChartRef || !this.categoryStocksData) return;
    if (this.categoryChartInstance) {
      this.categoryChartInstance.destroy();
    }
    const ctx = this.categoryStocksChartRef.nativeElement.getContext('2d');
    if (!ctx) return;
    this.categoryChartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: this.categoryStocksData.labels || [],
        datasets: this.categoryStocksData.datasets || []
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true, position: 'bottom' },
          tooltip: { enabled: true }
        }
      }
    });
  }

  navigateToCard(cardId: string) {
    if (!this.selectedWarehouseId) return;
    this.router.navigate([`/home/warehouses/${this.selectedWarehouseId}/implants/${cardId}`]);
  }
} 